import React, { useState, useEffect } from "react";
import { db } from "../../firebaseConfig";
import { collection, getDocs, query, where } from "firebase/firestore";

const PayoutCar = () => {
  const [cars, setCars] = useState([]);
  const [selectedCarID, setSelectedCarID] = useState("");
  const [selectedCar, setSelectedCar] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transfers, setTransfers] = useState(null);

  // Fetch all cars on mount
  useEffect(() => {
    const fetchCars = async () => {
      try {
        const carsCollection = collection(db, "cars");
        const carsSnapshot = await getDocs(carsCollection);
        const carsList = carsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCars(carsList);
      } catch (err) {
        console.error("Error fetching cars:", err.message);
        setError("Failed to load cars.");
      }
    };
    fetchCars();
  }, []);

  // When a car is selected, fetch its details and investments
  useEffect(() => {
    const fetchCarDetailsAndInvestments = async () => {
      if (!selectedCarID) return;
      
      const car = cars.find(c => c.id === selectedCarID);
      setSelectedCar(car || null);

      try {
        const investmentsQuery = query(
          collection(db, "investments"),
          where("carID", "==", selectedCarID)
        );
        const investmentsSnapshot = await getDocs(investmentsQuery);
        const investmentsList = investmentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setInvestments(investmentsList);
      } catch (err) {
        console.error("Error fetching investments:", err.message);
        setError("Failed to load investments.");
      }
    };

    fetchCarDetailsAndInvestments();
  }, [selectedCarID, cars]);

  // Calculate totals and percentages
  const totalRaised = investments.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const totalValue = selectedCar?.totalValue || 0;
  const companyPercent = totalValue ? (totalValue - totalRaised) / totalValue : 0;
  const investorPercent = totalValue ? totalRaised / totalValue : 0;
  const payout = parseInt(payoutAmount, 10) || 0;
  const companyAmount = payout * companyPercent;
  const investorsTotalAmount = payout * investorPercent;

  // Group investments by user and sum amounts
  let userInvestments = {};
  investments.forEach(inv => {
    const uid = inv.userID;
    const amt = inv.amount || 0;
    if (!userInvestments[uid]) userInvestments[uid] = 0;
    userInvestments[uid] += amt;
  });

  // Calculate distribution per investor
  let investorDistributions = [];
  for (const userId in userInvestments) {
    const userInv = userInvestments[userId];
    const userShare = totalRaised ? userInv / totalRaised : 0;
    const userPayout = investorsTotalAmount * userShare;
    investorDistributions.push({
      userId,
      invested: userInv,
      percentageOfInvestors: (userShare * 100).toFixed(2) + "%",
      payout: userPayout.toFixed(2)
    });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setTransfers(null);

    try {
      const response = await fetch("https://us-central1-gearshare-41e19.cloudfunctions.net/api/payout-car", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          carID: selectedCarID,
          payoutAmount: payout,
        }),
      });
    
      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }
    
      const result = await response.json();
      console.log("Payout successful:", result);
    } catch (error) {
      console.error("Error processing payout:", error);
    }
    
  };

  return (
    <div style={{ maxWidth: "800px", margin: "auto", padding: "20px" }}>
      <h1>Payout for Car</h1>
      {error && <div style={{ color: "red", marginBottom: "15px" }}>{error}</div>}
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: "10px" }}>
          <label>Car:</label><br />
          <select
            value={selectedCarID}
            onChange={(e) => setSelectedCarID(e.target.value)}
            required
            style={{ width: "100%", padding: "8px" }}
          >
            <option value="" disabled>
              Select a car
            </option>
            {cars.map(car => (
              <option key={car.id} value={car.id}>
                {car.name || car.id}
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: "10px" }}>
          <label>Payout Amount (in cents):</label><br />
          <input
            type="number"
            value={payoutAmount}
            onChange={(e) => setPayoutAmount(e.target.value)}
            required
            style={{ width: "100%", padding: "8px" }}
          />
        </div>
        <button type="submit" disabled={loading || !selectedCarID}>
          {loading ? "Processing..." : "Payout"}
        </button>
      </form>

      {selectedCar && (
        <div style={{ marginTop: "20px" }}>
          <h2>{selectedCar.name} Details</h2>
          <p><strong>Total Value:</strong> ${totalValue}</p>
          <p><strong>Total Raised:</strong> ${totalRaised}</p>
          <p>
            <strong>Company Ownership:</strong> {(companyPercent * 100).toFixed(2)}% 
            {payout ? ` - $${companyAmount.toFixed(2)} of $${(payoutAmount || 0)}` : ""}
          </p>
          <p>
            <strong>Investor Ownership:</strong> {(investorPercent * 100).toFixed(2)}% 
            {payout ? ` - $${investorsTotalAmount.toFixed(2)} of $${(payoutAmount || 0)}` : ""}
          </p>
        </div>
      )}

      {investorDistributions.length > 0 && (
        <div style={{ marginTop: "20px" }}>
          <h3>Investor Distributions</h3>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>User ID</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Invested Amount</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Ownership %</th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>Payout</th>
              </tr>
            </thead>
            <tbody>
              {investorDistributions.map(dist => (
                <tr key={dist.userId}>
                  <td style={{ border: "1px solid #ccc", padding: "8px" }}>{dist.userId}</td>
                  <td style={{ border: "1px solid #ccc", padding: "8px" }}>${dist.invested}</td>
                  <td style={{ border: "1px solid #ccc", padding: "8px" }}>{dist.percentageOfInvestors}</td>
                  <td style={{ border: "1px solid #ccc", padding: "8px" }}>${dist.payout}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {transfers && (
        <div style={{ marginTop: "20px" }}>
          <h3>Payouts Successful</h3>
          <pre>{JSON.stringify(transfers, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default PayoutCar;
